/**
 * Companion-conversation utilities: PDF download and "build sermon" handler.
 * Shared by the live ResponseView and the History sidebar so the same flow
 * works in both places.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Download a Lumera Companion conversation as a PDF file. */
export const downloadCompanionPdf = async (conversationId) => {
  if (!conversationId) return false;
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/lumera/conversation/${conversationId}/pdf`,
      { credentials: 'include' },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lumera-companion-${conversationId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    console.error('Companion PDF download failed:', e);
    return false;
  }
};

/**
 * Build a fresh Sermon Prep draft from this conversation. Returns the new
 * draft_id (or null on failure). Caller is responsible for navigation.
 */
export const buildSermonFromConversation = async (conversationId) => {
  if (!conversationId) return null;
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/lumera/conversation/${conversationId}/build-sermon`,
      { method: 'POST', credentials: 'include' },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data?.draft_id || null;
  } catch (e) {
    console.error('Build sermon from conversation failed:', e);
    return null;
  }
};

/**
 * Send the conversation PDF to Slidecon, our companion slide-deck app,
 * which converts the PDF into an editable slideshow. Returns the upstream
 * response shape `{project_id, title, slide_count, edit_url, view_url}`
 * (or `{error}` on failure). The caller is responsible for opening the
 * returned `edit_url` in a new tab.
 */
export const sendConversationToSlidecon = async (conversationId) => {
  if (!conversationId) return { error: 'Missing conversation id' };
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/lumera/conversation/${conversationId}/send-to-slidecon`,
      { method: 'POST', credentials: 'include' },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        error: data?.detail || `Slidecon error (${res.status})`,
        status: res.status,
        // 402 = subscription required (Slidecon is gated on any paid plan)
        upgradeRequired: res.status === 402,
      };
    }
    return data;
  } catch (e) {
    console.error('Send to Slidecon failed:', e);
    return { error: 'Network error reaching Slidecon' };
  }
};
